import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { useToast } from '@/components/ui/Toast.jsx';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Client-side preview (no holiday data). */
function previewPeriod(year, month, values) {
  const { payroll_period_mode: mode, payroll_period_start_day: startDay, payroll_period_end_day: endDay, payroll_period_working_days: wdCount, payroll_period_cross_month: crossMonth } = values;
  const pad = (n) => String(n).padStart(2, '0');

  if (!mode || mode === 'full_month') {
    const last = new Date(year, month, 0).getDate();
    return `${year}-${pad(month)}-01 – ${year}-${pad(month)}-${pad(last)}`;
  }

  if (mode === 'custom_date_range') {
    const sd = Number(startDay) || 1;
    const ed = Number(endDay) || 28;
    if (sd <= ed) {
      return `${year}-${pad(month)}-${pad(sd)} – ${year}-${pad(month)}-${pad(ed)}`;
    }
    // cross-month
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    return `${prevYear}-${pad(prevMonth)}-${pad(sd)} – ${year}-${pad(month)}-${pad(ed)}`;
  }

  if (mode === 'start_plus_working_days') {
    const sd = Number(startDay) || 1;
    const wd = Number(wdCount) || 26;
    if (crossMonth) {
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      return `${prevYear}-${pad(prevMonth)}-${pad(sd)} + ${wd} working days`;
    }
    return `${year}-${pad(month)}-${pad(sd)} + ${wd} working days`;
  }

  return '—';
}

export default function PayrollDatePage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['companySettings'], queryFn: () => api.get('/settings/company') });
  const { register, handleSubmit, control, watch, reset } = useForm({
    defaultValues: {
      payroll_period_mode: 'full_month',
      payroll_period_start_day: '',
      payroll_period_end_day: '',
      payroll_period_working_days: '',
      payroll_period_cross_month: false,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        payroll_period_mode: data.payroll_period_mode || 'full_month',
        payroll_period_start_day: data.payroll_period_start_day ?? '',
        payroll_period_end_day: data.payroll_period_end_day ?? '',
        payroll_period_working_days: data.payroll_period_working_days ?? '',
        payroll_period_cross_month: !!data.payroll_period_cross_month,
      });
    }
  }, [data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: (v) => api.put('/settings/company', {
      ...data,
      payroll_period_mode: v.payroll_period_mode,
      payroll_period_start_day: v.payroll_period_start_day ? Number(v.payroll_period_start_day) : null,
      payroll_period_end_day: v.payroll_period_end_day ? Number(v.payroll_period_end_day) : null,
      payroll_period_working_days: v.payroll_period_working_days ? Number(v.payroll_period_working_days) : null,
      payroll_period_cross_month: !!v.payroll_period_cross_month,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companySettings'] });
      toast({ variant: 'success', title: 'Payroll date settings saved' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  const values = watch();
  const mode = values.payroll_period_mode;
  const startDay = Number(values.payroll_period_start_day);
  const endDay = Number(values.payroll_period_end_day);
  const isCrossMonth = mode === 'custom_date_range' && startDay > 0 && endDay > 0 && startDay > endDay;

  const now = new Date();
  const previewYear = now.getFullYear();
  const previewMonth = now.getMonth() + 1; // 1-based
  const preview = useMemo(() => previewPeriod(previewYear, previewMonth, values), [previewYear, previewMonth, values]);

  if (isLoading) return null;

  return (
    <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-6">
      {/* Mode selector */}
      <FormField label="Period Mode">
        <Select {...register('payroll_period_mode')}>
          <option value="full_month">Full Month</option>
          <option value="custom_date_range">Custom Date Range</option>
          <option value="start_plus_working_days">Start + Working Days</option>
        </Select>
      </FormField>

      {/* Full Month — info only */}
      {mode === 'full_month' && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Each payroll period covers the 1st to the last day of the selected month.
        </p>
      )}

      {/* Custom Date Range */}
      {mode === 'custom_date_range' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Start Day (1–28)" hint="Day of month the period begins">
            <Input type="number" min="1" max="28" {...register('payroll_period_start_day')} />
          </FormField>
          <FormField label="End Day (1–28)" hint="Day of month the period ends">
            <Input type="number" min="1" max="28" {...register('payroll_period_end_day')} />
          </FormField>
          {isCrossMonth && (
            <p className="sm:col-span-2 text-xs text-amber-600 dark:text-amber-400">
              Since start day &gt; end day, this period spans two months (e.g. Oct {startDay} – Nov {endDay}).
            </p>
          )}
        </div>
      )}

      {/* Start + Working Days */}
      {mode === 'start_plus_working_days' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Start Day (1–28)" hint="Day of month the period begins">
            <Input type="number" min="1" max="28" {...register('payroll_period_start_day')} />
          </FormField>
          <FormField label="Working Days Count" hint="Number of working days in the period">
            <Input type="number" min="1" {...register('payroll_period_working_days')} />
          </FormField>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <Controller
              name="payroll_period_cross_month"
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
            Starts in previous month
          </label>
        </div>
      )}

      {/* Live preview */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-3 text-sm">
        <span className="font-medium text-slate-600 dark:text-slate-400">Preview for {MONTHS[previewMonth - 1]} {previewYear}: </span>
        <span className="font-semibold text-slate-900 dark:text-slate-100">{preview}</span>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </div>
    </form>
  );
}
